import { useEffect, useState } from 'react';
import { formatNumber, parseDecimal } from '@boatnav/core';

interface NumberFieldProps {
  value: number;
  onCommit: (value: number) => void;
  fractionDigits?: number;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  ariaLabel?: string;
}

/**
 * Shared decimal input (spec 07 numeric rule): inputmode=decimal, accepts `,`
 * and `.`, displays with a comma, commits the parsed value on blur.
 */
export function NumberField({
  value,
  onCommit,
  fractionDigits = 1,
  min,
  max,
  step,
  suffix,
  ariaLabel,
}: NumberFieldProps): JSX.Element {
  // Never render NaN: fall back to the min (or 0) if a bad value slips through.
  const safe = Number.isFinite(value) ? value : (min ?? 0);
  const [draft, setDraft] = useState(() => formatNumber(safe, fractionDigits));

  // Re-sync when the external value changes (e.g. unit toggle converts it).
  useEffect(() => {
    setDraft(formatNumber(Number.isFinite(value) ? value : (min ?? 0), fractionDigits));
  }, [value, fractionDigits, min]);

  const commit = (): void => {
    let n = parseDecimal(draft);
    if (n == null) {
      setDraft(formatNumber(value, fractionDigits));
      return;
    }
    if (min != null) n = Math.max(min, n);
    if (max != null) n = Math.min(max, n);
    onCommit(n);
    setDraft(formatNumber(n, fractionDigits));
  };

  return (
    <span className="numberfield">
      <input
        className="numberfield__input"
        inputMode="decimal"
        value={draft}
        step={step}
        aria-label={ariaLabel}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
      {suffix && <span className="numberfield__suffix">{suffix}</span>}
    </span>
  );
}
